import FiberNewIcon from '@mui/icons-material/FiberNew';
import MenuIcon from '@mui/icons-material/Menu';
import {
  ClickAwayListener,
  Collapse,
  Container,
  Hidden,
  IconButton,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import { Box } from '@mui/system';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ICoreUser } from '../../shared-interfaces/User/ICoreUser';
import { IUser } from '../../shared-interfaces/User/IUser';
import CookieService from '../../shared-services/CookieService';
import { Role } from '../../shared-types/Roles';
import { hasFeature, hasProduct } from '../contexts/InstitutionContext';
import LaunchDarklyContext from '../contexts/LaunchDarklyContext';
import UserSearch from '../UserSearch';
import DropDownNavigation from './DropDownNavigation';

interface ITopBar {
  user: IUser;
}

interface ITopBarLinkProps {
  to: string;
  label: string;
  newFeature?: boolean;
}

const useTopBarStyles = makeStyles(theme => ({
  dropdownText: {
    lineHeight: '15px',
  },
  newIconBadge: {
    right: '-12px',
  },
  toolbar: {
    paddingLeft: '10px',
    paddingRight: '10px',
    [theme.breakpoints.only('xs')]: {
      height: '130px',
      overflow: 'hidden',
    },
  },
  active: {
    backgroundColor: '#e0e0e0',
  },
  navPadding: {
    padding: '7px 20px',
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
  dropdownNavItem: {
    padding: '6px 20px 4px 30px',
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
}));

const isActive = (
  matchPath: string | [string],
  path: string,
  baseMatch = false,
) => {
  if (typeof matchPath === 'string') {
    path.search('');
    return baseMatch ? path.search(matchPath) === 0 : matchPath === path;
  }
  return baseMatch
    ? matchPath.some(x => path.search(x) === 0)
    : matchPath.indexOf(path) > -1;
};

const TopBarLink = ({ to, label, newFeature }: ITopBarLinkProps) => {
  const classes = useTopBarStyles();
  return (
    <Link to={to} className={classes.dropdownNavItem} tabIndex={0}>
      {newFeature && (
        <span style={{ margin: '0 0 0 -25px', float: 'left', padding: 0 }}>
          <FiberNewIcon color={'primary'} fontSize="small" />
        </span>
      )}
      <Typography className={classes.dropdownText}>{label}</Typography>
    </Link>
  );
};

const TopBar = (props: ITopBar) => {
  const classes = useTopBarStyles();
  const theme = useTheme();
  const launchDarkly = useContext(LaunchDarklyContext);
  const isSmDOwn = useMediaQuery(theme.breakpoints.only('xs'));

  const [path, setPath] = useState(window.location.pathname);
  const [activeDropdown, setActiveDropdown] = useState(-1);
  const [collapsed, setCollapsed] = useState(true);

  const location = useLocation();
  const scrollTrigger = useScrollTrigger();

  // We will get rid of this down the road
  const beaconDashboardOptOutCookie = !launchDarkly['home-can-opt-out']
    ? false
    : CookieService.Get('BeaconHomeOptOut') === 'true';

  useEffect(() => {
    setPath(location.pathname);
    setActiveDropdown(-1);
    setCollapsed(true);
  }, [location]);

  useEffect(() => {
    setCollapsed(true);
  }, [scrollTrigger]);

  return (
    <ClickAwayListener
      touchEvent="onTouchStart"
      onClickAway={() => setCollapsed(true)}
    >
      <div id="modern-main-nav">
        <Collapse in={!collapsed} collapsedSize={isSmDOwn ? 50 : 35}>
          <Container maxWidth="xl">
            <Box display="flex">
              <Box flexGrow={1}>
                <Grid
                  container
                  direction="row"
                  // component="nav"
                  alignItems="center"
                  className={classes.toolbar}
                >
                  <Hidden smUp>
                    <Grid item>
                      <IconButton
                        edge="start"
                        aria-label="menu"
                        onClick={() => setCollapsed(!collapsed)}
                        size="large"
                      >
                        <MenuIcon />
                      </IconButton>
                    </Grid>
                  </Hidden>
                  {props.user.isPeerAdvisor &&
                    launchDarkly['network-page'] &&
                    !beaconDashboardOptOutCookie && (
                      <>
                        <Grid item>
                          <NavLink
                            to="/"
                            tabIndex={0}
                            className={classes.navPadding}
                          >
                            <Typography>Home</Typography>
                          </NavLink>
                        </Grid>
                        <Grid item>
                          <NavLink
                            to="/groups"
                            tabIndex={0}
                            className={classes.navPadding}
                          >
                            <Typography>Groups</Typography>
                          </NavLink>
                        </Grid>
                        {hasProduct('Scheduling') && (
                          <Grid item>
                            <a
                              href="/beacon/students/advise/meetings/"
                              tabIndex={0}
                              className={
                                classes.navPadding +
                                (isActive('^/meetings', path, true)
                                  ? 'active'
                                  : '')
                              }
                            >
                              <Typography>Meetings</Typography>
                            </a>
                          </Grid>
                        )}
                      </>
                    )}
                  {props.user.isPeerAdvisor &&
                    !(
                      launchDarkly['network-page'] &&
                      !beaconDashboardOptOutCookie
                    ) && (
                      <>
                        <Grid item>
                          <NavLink
                            to="/groups"
                            tabIndex={0}
                            className={classes.navPadding}
                          >
                            <Typography>Groups</Typography>
                          </NavLink>
                        </Grid>
                        <Grid item className="nav-item">
                          <a
                            href="/beacon/students/advise"
                            tabIndex={0}
                            className={
                              'nav-padding ' +
                              (isActive('^/students', path, true) ? 'active' : '')
                            }
                          >
                            <Typography>Students</Typography>
                          </a>
                        </Grid>
                      </>
                    )}
                  {props.user.isExecutive && (
                    <>
                      <Grid item className="nav-item">
                        <a
                          href="/beacon/administrations"
                          tabIndex={0}
                          className={
                            'nav-padding ' +
                            (isActive('^/administrations', path, true)
                              ? 'active'
                              : '')
                          }
                        >
                          <Typography>Administrations</Typography>
                        </a>
                      </Grid>

                      <Grid item className="nav-item">
                        <DropDownNavigation
                          name="Reports"
                          active={isActive('^/reports', path, true)}
                          activeDropdown={activeDropdown}
                          setActiveDropdown={setActiveDropdown}
                          index={0}
                        >
                          <a
                            href="/beacon/reports"
                            className="nav-padding nav-item dropdown-nav-item"
                            tabIndex={0}
                          >
                            <Typography className={classes.dropdownText}>
                              Non-Cognitives
                            </Typography>
                          </a>
                          <a
                            href="/beacon/reports/notation"
                            className="nav-padding nav-item dropdown-nav-item"
                            tabIndex={0}
                          >
                            <Typography className={classes.dropdownText}>
                              Notation
                            </Typography>
                          </a>
                          {hasFeature('Metrics') && (
                            <a
                              href="/beacon/reports/metrics"
                              className="nav-padding nav-item dropdown-nav-item"
                              tabIndex={0}
                            >
                              <Typography className={classes.dropdownText}>
                                Metrics
                              </Typography>
                            </a>
                          )}
                        </DropDownNavigation>
                      </Grid>

                      {launchDarkly['management-permission'] && (
                        <Grid item className="nav-item">
                          <DropDownNavigation
                            name="Management"
                            active={isActive('^/management', path, true)}
                            activeDropdown={activeDropdown}
                            setActiveDropdown={setActiveDropdown}
                            index={1}
                          >
                            <TopBarLink
                              to="/management/users"
                              label="User Management"
                            />
                            <TopBarLink
                              to="/management/notation"
                              label="Notation Management"
                            />
                          </DropDownNavigation>
                        </Grid>
                      )}
                    </>
                  )}
                  {props.user.isAdministrator && (
                    <Grid item className="nav-item">
                      <DropDownNavigation
                        name="Settings"
                        active={isActive('^/settings', path, true)}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        index={2}
                      >
                        <div
                          className="nav-padding"
                          onClick={e => e.stopPropagation()}
                        >
                          <Typography>
                            <strong>Users</strong>
                          </Typography>
                        </div>

                        {!launchDarkly['management-permission'] && (
                          <TopBarLink
                            to="/management/users"
                            label="User Management"
                          />
                        )}

                        {hasProduct('CourseEvaluation') && (
                          <>
                            <a
                              href="/beacon/settings/tools/midtermgradeupload"
                              className="nav-padding nav-item dropdown-nav-item"
                              tabIndex={0}
                            >
                              <Typography className={classes.dropdownText}>
                                Manage Academic Record
                              </Typography>
                            </a>
                            <a
                              href="/beacon/settings/absence"
                              className="nav-padding nav-item dropdown-nav-item"
                              tabIndex={0}
                            >
                              <Typography className={classes.dropdownText}>
                                Manage Absences
                              </Typography>
                            </a>
                          </>
                        )}
                        <div
                          className="nav-padding"
                          onClick={e => e.stopPropagation()}
                        >
                          <Typography>
                            <strong>Notations</strong>
                          </Typography>
                        </div>
                        {!launchDarkly['management-permission'] && (
                          <TopBarLink
                            to="/management/notation"
                            label="Notation Management"
                          />
                        )}

                        <TopBarLink
                          to="/settings/notation/categories"
                          label="Notation Categories"
                        />
                        {hasFeature('Notations 1.5') && (
                          <a
                            href="/beacon/settings/reason"
                            className="nav-padding nav-item dropdown-nav-item"
                            tabIndex={0}
                          >
                            <Typography className={classes.dropdownText}>
                              Notation Reasons
                            </Typography>
                          </a>
                        )}
                        {hasFeature('DataInsights') && (
                          <a
                            href="/beacon/settings/trigger"
                            className="nav-padding nav-item dropdown-nav-item"
                            tabIndex={0}
                          >
                            <Typography className={classes.dropdownText}>
                              Triggers
                            </Typography>
                          </a>
                        )}
                        <TopBarLink
                          to="/settings/notation/widget"
                          label="Notation Widget"
                        />
                        <div
                          className="nav-padding"
                          onClick={e => e.stopPropagation()}
                        >
                          <Typography>
                            <strong>Assessments</strong>
                          </Typography>
                        </div>
                        <a
                          href="/beacon/administrations"
                          className="nav-padding nav-item dropdown-nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Manage Administrations
                          </Typography>
                        </a>
                        <a
                          href="/beacon/settings/customquestionbank/edit"
                          className="nav-padding nav-item dropdown-nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Custom Question Library
                          </Typography>
                        </a>
                        <a
                          href="/beacon/settings/recommendations"
                          className="nav-padding nav-item dropdown-nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Custom Assessment Recommendations
                          </Typography>
                        </a>
                        <div
                          className="nav-padding"
                          onClick={e => e.stopPropagation()}
                        >
                          <Typography>
                            <strong>Defaults</strong>
                          </Typography>
                        </div>
                        <a
                          href="/beacon/settings/systemmessages"
                          className="nav-padding nav-item dropdown-nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Default System Messages
                          </Typography>
                        </a>
                        <Link
                          to="/settings"
                          className="nav-padding nav-item"
                          tabIndex={0}
                          style={{ background: '#dcdcdc' }}
                        >
                          <Typography>
                            <strong>View All</strong>
                          </Typography>
                        </Link>
                      </DropDownNavigation>
                    </Grid>
                  )}
                  {props.user.isSupport && (
                    <Grid item className="nav-item">
                      <DropDownNavigation
                        name="Support"
                        active={isActive('^/support', path, true)}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        index={3}
                      >
                        <a
                          href={`${window.location.origin}/beacon/backiotomy`}
                          className="nav-padding nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Home
                          </Typography>
                        </a>
                        <a
                          href="/beacon/students/advise"
                          className="nav-padding nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Legacy Students
                          </Typography>
                        </a>
                        {launchDarkly['email-overview'] && (
                          <Link
                            to="/support/emailoverview"
                            className="nav-padding nav-item"
                          >
                            <Typography className={classes.dropdownText}>
                              Email Overview
                            </Typography>
                          </Link>
                        )}
                        {launchDarkly['support-import-users'] && (
                          <Link
                            to="/support/userimport"
                            className="nav-padding nav-item"
                          >
                            <Typography className={classes.dropdownText}>
                              User Import
                            </Typography>
                          </Link>
                        )}
                        <a
                          href={`${window.location.origin}/beacon/backiotomy/DigestEmails/Index`}
                          className="nav-padding nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Nightly Processes
                          </Typography>
                        </a>
                        <a
                          href={`${window.location.origin}/beacon/settings/globals`}
                          className="nav-padding nav-item"
                          tabIndex={0}
                        >
                          <Typography className={classes.dropdownText}>
                            Globals
                          </Typography>
                        </a>
                      </DropDownNavigation>
                    </Grid>
                  )}
                  {props.user.isStudent && !props.user.isAdvisor && (
                    <Grid item className="nav-item">
                      <a
                        href={`${window.location.origin}/beacon/students/me`}
                        tabIndex={0}
                        className={
                          'nav-padding ' +
                          (isActive('^/beacon/students/me', path)
                            ? 'active'
                            : '')
                        }
                      >
                        <Typography>Me</Typography>
                      </a>
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Box
                style={{
                  width: '250px',
                  paddingTop: '3px',
                  margin: '0 8px 7px 0',
                }}
              >
                <UserSearch
                  placeHolder="Find a Student"
                  onSelect={u => {
                    window.location.replace(
                      `${
                        window.location.origin
                      }/beacon/students/advise/student/${
                        (u as ICoreUser).externalId
                      }`,
                    );
                  }}
                  size="small"
                  variant="standard"
                  roleRestriction={[Role.Student, Role.PeerAdvisor]}
                  inNetwork={!props.user.isGeneralist}
                />
              </Box>
            </Box>
          </Container>
        </Collapse>
      </div>
    </ClickAwayListener>
  );
};

export default TopBar;
